import type { CamposDeLaFila, ConstruccionDeInforme } from "@/lib/informes";
import { textoDeDecimal, textoDeNumero } from "@/lib/montos";
import { aDosDecimales, numeroEscrito } from "@/lib/numeroEscrito";
import type { ItemDeTerreno } from "@/lib/terreno";

/* ---------------------------------------------------------------------------
   La pestaña "Construcciones": traducción formulario ↔ API.

   VIVE APARTE DEL COMPONENTE por lo mismo que sus dos gemelos: acá está el
   riesgo real de la pantalla, que es el punto de miles. Un "16.000" leído como
   decimal deja la construcción valiendo la milésima parte, en un informe
   pericial.
--------------------------------------------------------------------------- */

/**
 * Una construcción mientras se escribe.
 *
 * TODO ES STRING porque eso es lo que hay en un `<input>`: guardarlo como
 * número obligaría a decidir qué es un campo a medio teclear ("-", "1,") y la
 * respuesta siempre sería borrar lo que la persona está escribiendo.
 */
export interface FormularioDeConstruccion {
  grupo: string;
  tipo_edificacion: string;
  anio: string;
  superficie_m2: string;
  clase: string;
  vida_util_total: string;
  vida_util_residual: string;
  valor_reposicion_uf_m2: string;
  valor_residual_uf_m2: string;
  ajuste_comercial_pct: string;
  nota_terreno: string;
}

/**
 * El formulario recién abierto.
 *
 * NINGÚN CAMPO ARRANCA CON VALOR: el request no exige más que el tipo de
 * edificación, y una superficie no tiene un valor razonable que suponer — se
 * mide. Es la misma decisión que en instalaciones y al revés que en
 * maquinaria, donde las unidades se cuentan y el "1" es el caso normal.
 */
export const CONSTRUCCION_VACIA: FormularioDeConstruccion = {
  grupo: "",
  tipo_edificacion: "",
  anio: "",
  superficie_m2: "",
  clase: "",
  vida_util_total: "",
  vida_util_residual: "",
  valor_reposicion_uf_m2: "",
  valor_residual_uf_m2: "",
  ajuste_comercial_pct: "",
  nota_terreno: "",
};

/** Carga el formulario con una construcción ya guardada, para corregirla. */
export function formularioDesdeConstruccion(
  construccion: ConstruccionDeInforme,
): FormularioDeConstruccion {
  return {
    grupo: construccion.grupo ?? "",
    tipo_edificacion: construccion.tipo_edificacion,
    anio: textoDeNumero(construccion.anio),
    // Los decimales llegan como "180.00": se muestran sin el relleno de ceros
    // y con coma. Con el punto crudo, reabrir la fila y guardarla sin tocar
    // nada cambiaría su superficie.
    superficie_m2: textoDeDecimal(construccion.superficie_m2),
    clase: construccion.clase ?? "",
    vida_util_total: textoDeNumero(construccion.vida_util_total),
    vida_util_residual: textoDeNumero(construccion.vida_util_residual),
    valor_reposicion_uf_m2: textoDeDecimal(construccion.valor_reposicion_uf_m2),
    valor_residual_uf_m2: textoDeDecimal(construccion.valor_residual_uf_m2),
    ajuste_comercial_pct: textoDeDecimal(construccion.ajuste_comercial_pct),
    nota_terreno: construccion.nota_terreno ?? "",
  };
}

/**
 * Precarga el formulario con una construcción que el encargado creó en la App
 * de terreno y que todavía no es una fila del cuadro.
 *
 * Solo trae lo que la App captura —tipo, clase, año y superficie— y nada de
 * valorización: eso lo pone quien tasa. El año entra solo si son CUATRO
 * dígitos y la superficie sin la unidad ("54,5 m²" → "54,5"): `numeroEscrito`
 * lee esa coma como decimal, así que el valor no cambia al guardarlo.
 */
export function formularioDesdeElItemDeTerreno(item: ItemDeTerreno): FormularioDeConstruccion {
  const dato = (etiqueta: string) => item.datos.find((d) => d.etiqueta === etiqueta)?.valor ?? "";
  const anio = dato("Año").trim();

  return {
    ...CONSTRUCCION_VACIA,
    tipo_edificacion: dato("Tipo") || item.nombre,
    clase: dato("Clase"),
    anio: /^\d{4}$/.test(anio) ? anio : "",
    superficie_m2: dato("Superficie").replace(/[^\d,]/g, ""),
  };
}

/**
 * El cuerpo que viaja al servidor.
 *
 * Un campo vacío se manda como `null` y NO se omite: omitirlo en un PATCH
 * significa "no lo toques" (el request usa `sometimes`), así que vaciar un
 * valor no borraría nada.
 *
 * `grupo` y `clase` son TEXTO y no números, así que su vacío también viaja
 * como `null`: una cadena vacía en la base sería un segundo representante de
 * "no tiene grupo".
 */
export function camposDesdeConstruccion(formulario: FormularioDeConstruccion): CamposDeLaFila {
  return {
    grupo: textoONulo(formulario.grupo),
    tipo_edificacion: formulario.tipo_edificacion.trim(),
    anio: numeroEscrito(formulario.anio),
    superficie_m2: numeroEscrito(formulario.superficie_m2),
    clase: textoONulo(formulario.clase),
    vida_util_total: numeroEscrito(formulario.vida_util_total),
    vida_util_residual: numeroEscrito(formulario.vida_util_residual),
    valor_reposicion_uf_m2: numeroEscrito(formulario.valor_reposicion_uf_m2),
    valor_residual_uf_m2: numeroEscrito(formulario.valor_residual_uf_m2),
    ajuste_comercial_pct: numeroEscrito(formulario.ajuste_comercial_pct),
    nota_terreno: textoONulo(formulario.nota_terreno),
  };
}

/**
 * El "Valor estimado" que la pantalla previsualiza mientras se escribe.
 *
 * ES EL GEMELO DE `InformeConstruccion::totalUf()` (api): el valor RESIDUAL por
 * la superficie, con el ajuste comercial aplicado UNA vez sobre el total.
 * CAMBIAR UNO OBLIGA A CAMBIAR EL OTRO EN EL MISMO COMMIT, o la pantalla
 * mostraría una cifra distinta de la que queda guardada — la peor forma de
 * equivocarse, porque las dos se ven bien por separado.
 *
 * - Sin superficie o sin valor residual es `null`: suponer un "1" daría el
 *   unitario con cara de total, y la reposición NO reemplaza al residual.
 * - Sin ajuste cargado devuelve el residual tal cual: `null` es "nadie lo
 *   corrigió", no "lo corrigió a la nada".
 * - El ajuste CON SIGNO descuenta (−8 % resta), y no se recorta un resultado
 *   negativo: el número raro es la señal de que hay que revisar.
 * - Cada paso se redondea a dos decimales, como el PHP.
 *
 * El prototipo calcula otra cosa (`sup × máx(residual, reposición × vida
 * residual ÷ vida total)`); el servidor manda y esta función lo sigue a ÉL.
 */
export function totalUfDeLaConstruccion(
  formulario: Pick<
    FormularioDeConstruccion,
    "superficie_m2" | "valor_residual_uf_m2" | "ajuste_comercial_pct"
  >,
): number | null {
  const superficie = numeroEscrito(formulario.superficie_m2);
  const residualPorM2 = numeroEscrito(formulario.valor_residual_uf_m2);

  if (superficie === null || residualPorM2 === null) return null;

  const residual = aDosDecimales(residualPorM2 * superficie);
  const ajuste = numeroEscrito(formulario.ajuste_comercial_pct) ?? 0;

  return aDosDecimales(residual * (1 + ajuste / 100));
}

/** Un texto recortado, o `null` si no quedó nada. */
function textoONulo(valor: string): string | null {
  const limpio = valor.trim();

  return limpio === "" ? null : limpio;
}
